import React from 'react';

import { Button } from '@components/Button';
import { SpinnerModal } from '@components/SpinnerModal';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { useDeleteFriendInvited } from '@hooks/mutation/useDeleteFriendInvtited';
import { useDeleteFriendInviting } from '@hooks/mutation/useDeleteFriendInvtiting';
import { usePatchFriendInvited } from '@hooks/mutation/usePatchFriendInvited';
import { ContactForm } from '@models/Contact';
import { FriendContact, FriendStatus } from '@models/Friend';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    request: '連携リクエスト',
    requested: '連携リクエスト送信済み',
    cancel: 'リクエストを取り消し',
    accept: '承認する',
    ignore: '無視する',
    accepted: '承認した',
    beAccepted: '承認された',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  itemWrapper: {
    ...common.flexInline,
    columnGap: 1,
  },
  clickableContainer: {
    ...common.flexColumnCenter,
    rowGap: 6,
    minWidth: 217,
  },
  itemButton: {
    ...common.flexCenter,
    ...common.bigText,
    height: 38,
    padding: '0px 10px',
    border: `1px solid ${theme.palette.accent.main}`,
    background: theme.palette.background.paper,
    color: theme.palette.accent.main,
  },
  itemContainer: {
    ...common.flexInline,
    ...common.itemsCenter,
    ...common.justifyBetween,
    flex: 1,
    padding: '20px 20px',
    backgroundColor: theme.palette.grey[100],
    [theme.breakpoints.down('xs')]: {
      width: '297px',
      padding: '1rem',
    },
  },
  itemIndicator: {
    backgroundColor: theme.palette.grey[100],
    width: 12,
    flex: '0 0 12px',
    borderRadius: 2,
  },
  link: {
    ...common.smallerText,
    color: theme.palette.grey[700],
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  disabled: { cursor: 'not-allowed' },
}));

export type FriendInvitationRoles = 'requester' | 'recipient';

interface InvitationItemProps {
  role: FriendInvitationRoles;
  contact: FriendContact | undefined | null;
  status: FriendStatus;
  id: number;
}

export const InvitationItem = React.forwardRef<HTMLDivElement, InvitationItemProps>(
  (props, ref) => {
    const { id, role, contact, status } = props;
    const classes = {
      ...useCommonStyles(),
      ...useStyles(),
    };

    const patchFriendInvited = usePatchFriendInvited(id);
    const deleteFriendInviting = useDeleteFriendInviting(id);
    const deleteFriendInvited = useDeleteFriendInvited(id);

    const contactToDisplay: ContactForm = {
      zip_code: contact?.zip_code ?? '招待されたMembersの郵便番号',
      address1: contact?.address1 ?? '招待されたMembersの住所',
      address1_cn: '',
      address2: contact?.address2 ?? '',
      company_name: contact?.company_name ?? '招待されたMembersの会社名',
      english_name: contact?.english_name ?? '招待されたMembersの英字社名',
      principal_name: contact?.display_name ?? '招待されたMembersの代表名',
      email: contact?.email ?? '招待されたMembersのメール',
      social_credit_id: '',
      phone: '',
      sns_id: '',
      user: undefined,
    };

    return (
      <>
        <SpinnerModal
          open={
            patchFriendInvited.isLoading ||
            deleteFriendInviting.isLoading ||
            deleteFriendInvited.isLoading
          }
        />
        <div ref={ref} className={classes.itemWrapper}>
          <div className={classes.itemIndicator} />
          <div className={classes.itemContainer}>
            <ContactDisplay
              desensitized={status !== 'CONFIRMED'}
              contact={contactToDisplay}
              variant="big"
            />

            <div className={classes.clickableContainer}>
              {role === 'requester' && (
                <>
                  {status === 'AWAIT_CONFIRMATION' && (
                    <>
                      <div className={clsx(classes.itemButton, classes.disabled)}>
                        {strings.requested}
                      </div>
                      <div
                        className={classes.link}
                        onClick={async () => await deleteFriendInviting.mutateAsync()}
                      >
                        {strings.cancel}
                      </div>
                    </>
                  )}
                  {status === 'CONFIRMED' && (
                    <div className={clsx(classes.bigText, classes.textGray700)}>
                      {strings.beAccepted}
                    </div>
                  )}
                </>
              )}
              {role === 'recipient' && (
                <>
                  {status === 'AWAIT_CONFIRMATION' && (
                    <>
                      <Button
                        text={<div className={classes.bigText}>{strings.accept}</div>}
                        color="accent"
                        onClick={async () =>
                          await patchFriendInvited.mutateAsync({
                            status: 'CONFIRMED',
                          })
                        }
                        isLoading={patchFriendInvited.isLoading}
                      />
                      <Button
                        text={<div className={classes.bigText}>{strings.ignore}</div>}
                        color="accent-outlined"
                        onClick={async () => await deleteFriendInvited.mutateAsync()}
                        isLoading={deleteFriendInvited.isLoading}
                      />
                    </>
                  )}
                  {status === 'CONFIRMED' && (
                    <div className={clsx(classes.bigText, classes.textGray700)}>
                      {strings.accepted}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </>
    );
  },
);
